import { useEffect, useState } from 'react';

import { EVENT_SHOW_BUCKET, showColumns } from '../detail/event-detail';
import { buildShow, type EventShow } from '../event-show';
import { getPrefetched } from '../static-prefetch';
import { supabase, type DbEvent } from '../supabase';

type Fetched = { slug: string; show: EventShow | null; error: string | null };

/**
 * The show behind an /[lang]/events/<show_slug> URL. Until the client fetch
 * lands, it returns the record the static export baked into the page
 * (getPrefetched — during the export itself, the one generateStaticParams
 * stashed), so the first render matches the HTML. Results are keyed by slug,
 * so a screen reused for another show never shows the previous one's data. A
 * failed refresh keeps the baked record on screen rather than blanking it.
 * (Riviera's own hook also resolves its old per-date URLs.)
 */
export function useEventShow(slug: string | undefined) {
  const [fetched, setFetched] = useState<Fetched | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    supabase
      .from('events_visible')
      .select(showColumns())
      .eq('show_slug', slug)
      .then(({ data, error }) => {
        if (cancelled) return;
        setFetched({
          slug,
          show: error ? null : buildShow((data ?? []) as unknown as DbEvent[]),
          error: error?.message ?? null,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (!slug) return { show: null, loading: false, error: null };
  const current = fetched?.slug === slug ? fetched : null;
  const show = current?.show ?? getPrefetched<EventShow>(EVENT_SHOW_BUCKET, slug) ?? null;
  return { show, loading: !current && !show, error: current?.error ?? null };
}
